import React, { useState, useEffect } from "react"
import { uniq, cloneDeep } from "lodash"
import { useShallow } from "zustand/react/shallow"
import { findArmsByTitleAndDescription } from "../../utils"

// components
import { TextInput } from "components-design-system"
import Checkbox from "components/Checkbox/Checkbox"
import Modal from "components/Modal/Modal"
import Note from "components/Note/Note"

// context
import useResultsTableViewStore from "../../../../hooks/stores/useResultsTableViewStore"
import useResultsTableArmsStore from "../../../../hooks/stores/useResultsTableArmsStore"
import useAuthoringDataStore from "../../../../hooks/stores/useAuthoringDataStore"
import useAuthoringViewStore from "../../../../hooks/stores/useAuthoringViewStore"

import "./ArmOverrideModal.scss"

const sources = {
  "participant-flow": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          // disabled: true,
          label: "Participant flow",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          // disabled: true,
          label: "Subject disposition",
          key: "pf",
        },
        {
          // disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          // disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
  "baseline-characteristics": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          label: "Participant flow",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          label: "Subject disposition",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
  "outcome-measures": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          label: "Participant flow",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          label: "Subject disposition",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
  "adverse-events": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          label: "Participant flow",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          label: "Subject disposition",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
}

const defaultValues = {
  "participant-flow": {
    ctg: {
      pf: true,
      bc: false,
      om: false,
      ae: false,
    },
    eudract: {
      pf: true,
      bc: true,
      om: true,
      ae: false,
    },
  },
  "baseline-characteristics": {
    ctg: {
      pf: false,
      bc: true,
      om: false,
      ae: false,
    },
    eudract: {
      pf: true,
      bc: true,
      om: true,
      ae: false,
    },
  },
  "outcome-measures": {
    ctg: {
      pf: false,
      bc: false,
      om: true,
      ae: false,
    },
    eudract: {
      pf: true,
      bc: true,
      om: true,
      ae: false,
    },
  },
  "adverse-events": {
    ctg: {
      pf: false,
      bc: false,
      om: false,
      ae: true,
    },
    eudract: {
      pf: false,
      bc: false,
      om: false,
      ae: true,
    },
  },
}

const schema = [
  {
    type: "textarea",
    label: "Title",
    key: "title",
  },
  {
    type: "textarea",
    label: "Description",
    key: "description",
  },
]

const stripIds = (id) => {
  const arrs = id.split(".")
  return arrs[arrs.length - 1]
}

const ArmOverrideModal = ({ open, onCancel, arm, source }) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const { tableView } = useResultsTableViewStore()
  const [originalArmData, setOriginalArmData] = React.useState(null)
  const { formData, setFormData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      setFormData: state.setFormData,
    }))
  )

  const { refreshFormData, setRefreshFormData } = useAuthoringViewStore(
    useShallow((state) => ({
      refreshFormData: state.refreshFormData,
      setRefreshFormData: state.setRefreshFormData,
    }))
  )

  const { baselineSas, setBaselineSas } = useResultsTableArmsStore()
  // const [sources, setSources] = useState({})
  const [armData, setArmData] = useState({
    title: "",
    description: "",
  })

  const [applicableSections, setApplicableSections] = useState({
    eudract: {
      pf: false,
      bc: false,
      om: false,
      ae: false,
    },
    ctg: {
      pf: false,
      bc: false,
      om: false,
      ae: false,
    },
  })

  useEffect(() => {
    setApplicableSections(defaultValues[source])
  }, [defaultValues])

  useEffect(() => {
    if (!open) {
      setApplicableSections(defaultValues[source])
    }
  }, [open])

  useEffect(() => {
    if (open) {
      setArmData({
        title: arm.title,
        description: arm.description,
      })
      setOriginalArmData(cloneDeep(arm))
    }
  }, [open])

  const updateArm = (targetArm) => {
    if (!armData) return
    targetArm.title = armData.title
    targetArm.description = armData.description
  }

  const findArmsAndUpdate = (originalArms, armsToUpdate) => {
    originalArms.forEach((arm, armIndex) => {
      let foundIndex = armsToUpdate.findIndex((armToUpdate) => {
        return armToUpdate.id === arm.id
      })

      if (foundIndex !== -1) {
        updateArm(originalArms[armIndex])
      }
    })
  }

  const onSave = () => {
    setIsSubmitLoading(true)
    let rawArmId = arm.id.split(".").slice(-1)[0]

    let tempData = { ...formData }
    Object.keys(applicableSections).forEach((registry) => {
      let sections = applicableSections[registry]
      if (sections.pf) {
        findPfArmAndSave(tempData, registry, rawArmId)
      }
      if (sections.bc) {
        findBcArmAndSave(tempData, registry, rawArmId)
      }
      if (sections.om) {
        findOmArmAndSave(tempData, registry, rawArmId)
      }
      if (sections.ae) {
        findAeArmAndSave(tempData, registry, rawArmId)
      }
    })

    setFormData(tempData)
    onCancel()
    setRefreshFormData(!refreshFormData)
    setIsSubmitLoading(false)
  }

  const findPfArmAndSave = (data, registry, rawArmId) => {
    let pfTable = data?.results?.participant_flow?.participant_flow_table
    if (registry === "ctg" && pfTable?.["global_arms"]) {
      let foundIndex = pfTable["global_arms"].findIndex((arm) => stripIds(arm?.id) === rawArmId)
      const foundArmsByTitleAndDesc = findArmsByTitleAndDescription(pfTable["global_arms"], originalArmData)
      if (foundIndex !== -1) {
        updateArm(pfTable["global_arms"][foundIndex])
      } else if (foundArmsByTitleAndDesc.length > 0) {
        findArmsAndUpdate(pfTable["global_arms"], foundArmsByTitleAndDesc)
      }
    } else if (registry === "eudract" && pfTable?.["eudract_periods"]) {
      pfTable["eudract_periods"].forEach((period) => {
        let foundIndex = period.arms.findIndex((arm) => stripIds(arm?.id) === rawArmId)
        const foundArmsByTitleAndDesc = findArmsByTitleAndDescription(period.arms, originalArmData)
        if (foundIndex !== -1) {
          updateArm(period.arms[foundIndex])
        } else if (foundArmsByTitleAndDesc.length > 0) {
          findArmsAndUpdate(period.arms, foundArmsByTitleAndDesc)
        }
      })
    }
  }

  const findBcArmAndSave = (data, registry, rawArmId) => {
    let bcTable = data?.results?.baseline_characteristics?.baseline_characteristics_table
    if (bcTable?.[registry]?.arms) {
      let foundIndex = bcTable[registry].arms.findIndex((arm) => stripIds(arm?.id) === rawArmId)
      const foundArmsByTitleAndDesc = findArmsByTitleAndDescription(bcTable[registry].arms, originalArmData)

      if (foundIndex !== -1) {
        updateArm(bcTable[registry].arms[foundIndex])
      } else if (foundArmsByTitleAndDesc.length > 0) {
        findArmsAndUpdate(bcTable[registry].arms, foundArmsByTitleAndDesc)
      } else if (bcTable?.[registry]?.subject_analysis_sets) {
        let foundIndex = bcTable[registry].subject_analysis_sets.findIndex((arm) => stripIds(arm?.id) === rawArmId)
        const foundSasByTitleAndDesc = findArmsByTitleAndDescription(
          bcTable?.[registry]?.subject_analysis_sets,
          originalArmData
        )

        if (foundIndex !== -1) {
          updateArm(bcTable[registry].subject_analysis_sets[foundIndex])
          setBaselineSas(uniq([...baselineSas, bcTable[registry].subject_analysis_sets[foundIndex]], "id"))
        } else if (foundSasByTitleAndDesc.length > 0) {
          findArmsAndUpdate(bcTable?.[registry]?.subject_analysis_sets, foundSasByTitleAndDesc)
        }
      }
    }
  }

  const findOmArmAndSave = (data, registry, rawArmId) => {
    let omTable = data?.results?.outcome_measures?.outcome_measures_table

    if (omTable?.[registry]?.["outcome_measures"]) {
      omTable[registry]["outcome_measures"].forEach((om) => {
        let foundIndex = om.arms.findIndex((arm) => stripIds(arm?.id) === rawArmId)
        const foundArmsByTitleAndDesc = findArmsByTitleAndDescription(om.arms, originalArmData)

        if (foundIndex !== -1) {
          updateArm(om.arms[foundIndex])
        } else if (foundArmsByTitleAndDesc.length > 0) {
          findArmsAndUpdate(om.arms, foundArmsByTitleAndDesc)
        } else if (om?.subject_analysis_sets) {
          let foundIndex = om.subject_analysis_sets.findIndex((arm) => stripIds(arm?.id) === rawArmId)
          const foundSasByTitleAndDesc = findArmsByTitleAndDescription(om.subject_analysis_sets, originalArmData)

          if (foundIndex !== -1) {
            updateArm(om.subject_analysis_sets[foundIndex])
          } else if (foundSasByTitleAndDesc.length > 0) {
            findArmsAndUpdate(om.subject_analysis_sets, foundSasByTitleAndDesc)
          }
        }
      })
    }
  }

  const findAeArmAndSave = (data, registry, rawArmId) => {
    let ae = data?.results?.adverse_events?.adverse_events_table
    if (ae?.[registry]?.groups) {
      let foundIndex = ae[registry].groups.findIndex((arm) => stripIds(arm?.id) === rawArmId)
      const foundArmsByTitleAndDesc = findArmsByTitleAndDescription(ae[registry].groups, originalArmData)

      if (foundIndex !== -1) {
        updateArm(ae[registry].groups[foundIndex])
      } else if (foundArmsByTitleAndDesc.length > 0) {
        findArmsAndUpdate(ae[registry].groups, foundArmsByTitleAndDesc)
      }
    }
  }

  const onCheck = (key, value, registry) => {
    if ((key === "pf" || key === "bc" || key === "om") && registry === "eudract") {
      setApplicableSections({
        ...applicableSections,
        [registry]: {
          ...applicableSections[registry],
          pf: value,
          bc: value,
          om: value,
        },
      })
    } else {
      setApplicableSections({
        ...applicableSections,
        [registry]: {
          ...applicableSections[registry],
          [key]: value,
        },
      })
    }
  }

  let tableTitle = source?.replace("-", " ")
  if (tableView === "eudract" && tableTitle === "participant flow") tableTitle = "subject disposition"

  return (
    <Modal
      title={`Edit ${tableTitle} arm`}
      open={open}
      onCancel={onCancel}
      hideImage={true}
      width={500}
      onOk={onSave}
      confirmLoading={isSubmitLoading}
      okText={"Save changes"}
      destroyOnClose={true}
    >
      <div className="arm-override-modal-container">
        <div className="arm-properties">
          {schema.map((field) => {
            return (
              <TextInput
                key={field.key}
                type={schema.type}
                label={field.label}
                onChange={(e) => setArmData({ ...armData, [field.key]: e.target.value })}
                value={armData[field.key]}
                autoSize={true}
              />
            )
          })}
        </div>
        <div className="registries-container">
          {sources?.[source]?.map((registry) => {
            return (
              <div
                key={registry.registry}
                className="registry-container"
              >
                <p>{registry.label}</p>
                <div>
                  {registry.items.map((checkbox) => {
                    return (
                      <Checkbox
                        key={registry.registry + checkbox.key}
                        label={checkbox.label}
                        disabled={checkbox.disabled}
                        checked={applicableSections?.[registry.registry]?.[checkbox.key]}
                        onChange={(e) => onCheck(checkbox.key, e.target.checked, registry.registry)}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        <Note
          style={{ marginTop: 24 }}
          type="note"
          text="For EudraCT, all linked Arms will be updated. If there is a need to update an arm independently, please create a Subject Analysis Set"
        />
      </div>
    </Modal>
  )
}

export default ArmOverrideModal
